// The public marketing website (deltasa.net; migration 057). What a Super Admin configures and what the website
// reads. Nothing here carries tenant data, reseller rates or anything a package does not publish.
import type { BillingPeriod, FeatureKey, Limits, PackageKind } from "./packages.js";
import type { SignupMode } from "./platform.js";

/** Contact details shown on the website. Every field is optional; an empty field is simply not shown. */
export interface WebsiteContact {
  companyEn: string; companyAr: string;
  phone: string; whatsapp: string; email: string;
  addressEn: string; addressAr: string;
  hoursEn: string; hoursAr: string;
  /** Social pages: full https addresses on the network's own site, each optional (request of 31 Oct 2026). */
  socialX: string; socialInstagram: string; socialLinkedin: string; socialFacebook: string;
  socialTiktok: string; socialSnapchat: string; socialYoutube: string;
}
/** The social networks the website can link to, with the host names an address must be on. */
export const WEBSITE_SOCIAL = {
  socialX: ["x.com", "twitter.com"], socialInstagram: ["instagram.com"], socialLinkedin: ["linkedin.com"], socialFacebook: ["facebook.com"],
  socialTiktok: ["tiktok.com"], socialSnapchat: ["snapchat.com"], socialYoutube: ["youtube.com"]
} as const satisfies Partial<Record<keyof WebsiteContact, readonly string[]>>;
export type WebsiteSocialKey = keyof typeof WEBSITE_SOCIAL;
export const WEBSITE_SOCIAL_FIELDS = Object.keys(WEBSITE_SOCIAL) as WebsiteSocialKey[];
export const WEBSITE_CONTACT_FIELDS = ["companyEn", "companyAr", "phone", "whatsapp", "email", "addressEn", "addressAr", "hoursEn", "hoursAr",
  "socialX", "socialInstagram", "socialLinkedin", "socialFacebook", "socialTiktok", "socialSnapchat", "socialYoutube"] as const satisfies readonly (keyof WebsiteContact)[];
export const WEBSITE_CONTACT_MAX: Record<keyof WebsiteContact, number> = {
  companyEn: 200, companyAr: 200, phone: 20, whatsapp: 20, email: 200, addressEn: 400, addressAr: 400, hoursEn: 200, hoursAr: 200,
  socialX: 300, socialInstagram: 300, socialLinkedin: 300, socialFacebook: 300, socialTiktok: 300, socialSnapchat: 300, socialYoutube: 300
};
/** True when the address is https on the network's own host (or a subdomain of it), with no user name or port. */
export const websiteSocialOk = (key: WebsiteSocialKey, value: string): boolean => {
  let u: URL;
  try { u = new URL(value); } catch { return false; }
  if (u.protocol !== "https:" || u.username || u.password || u.port) return false;
  const h = u.hostname.toLowerCase();
  return WEBSITE_SOCIAL[key].some((d) => h === d || h.endsWith(`.${d}`));
};
/** A phone or WhatsApp number as entered: digits with an optional leading +, 8 to 15 digits (spaces allowed). */
export const WEBSITE_PHONE = /^\+?[0-9 ]{8,20}$/;

/** A package as the website shows it: display fields only (no id, code, version, subscriptions or reseller data). */
export interface WebsitePackage {
  nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string;
  kind: PackageKind; trialDays: number | null; billingPeriod: BillingPeriod | null; currency: string | null; price: string | null;
  features: FeatureKey[]; limits: Limits;
}

/** What website_public() returns. */
export interface WebsitePublicData {
  contact: Partial<WebsiteContact>;
  demo: { enabled: boolean; minutes: number };
  signupMode: SignupMode;
  packages: WebsitePackage[];
}

/** The Super Admin's Website screen. */
export interface PlatformWebsiteDto {
  contact: WebsiteContact;
  /** Every package, with whether it can be shown (active and public) and whether it is chosen for the website. */
  packages: { id: string; code: string; nameEn: string; nameAr: string; active: boolean; public: boolean; website: boolean }[];
  /** Read-only here: the demo and signup are changed on their own screens. */
  demoEnabled: boolean;
  signupMode: SignupMode;
  updatedAt: string;
}
/** Contact details as sent: the social fields may be left out by an older screen (they are then kept as they were). */
export interface PlatformWebsiteInput { contact: Omit<WebsiteContact, WebsiteSocialKey> & Partial<Pick<WebsiteContact, WebsiteSocialKey>>; packageIds?: string[] }
